/**
 * Is this a small screen: a phone, or a tablet held upright? Under 920 px a
 * page stops being a page and becomes an app screen.
 */
import { useEffect, useState } from "react";

const SMALL_QUERY = "(max-width: 920px)";
/** A finger rather than a mouse: phones and tablets. */
export const TOUCH_QUERY = "(pointer: coarse)";

/** Whether a media query matches, kept up to date as the window changes. */
export function useMediaQuery(query: string): boolean {
  const [hit, setHit] = useState(() => typeof window !== "undefined" && !!window.matchMedia?.(query)?.matches);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return;
    const read = () => setHit(mq.matches);
    read();
    mq.addEventListener?.("change", read);
    return () => mq.removeEventListener?.("change", read);
  }, [query]);
  return hit;
}

export function useSmallScreen(): boolean {
  return useMediaQuery(SMALL_QUERY);
}

/** A phone, held either way up to 700 px: the Watch page's tabs and the Room's sheet are a phone's. */
export const PHONE_QUERY = "(max-width: 700px)";

export function usePhone(): boolean {
  return useMediaQuery(PHONE_QUERY);
}

/**
 * The page reaches under the notch and the home bar while it is open, so the
 * safe-area insets it keeps clear of are real. The address's own viewport tag
 * is left as it was when the page goes.
 */
export function useViewportCover(): void {
  useEffect(() => {
    const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
    if (!meta || /viewport-fit\s*=\s*cover/.test(meta.content)) return;
    const had = meta.content;
    meta.content = `${had}, viewport-fit=cover`;
    return () => {
      meta.content = had;
    };
  }, []);
}
